import { logger } from '@/src/internal/logger';

let version = 0;

const listeners = new Set<() => void>();

/**
 * Subscribes a listener function to changes in the urql cache version. The listener will be called
 * whenever the cache version is incremented via {@link invalidateUrqlCache}, which signals that a
 * new {@link UrqlGraphqlClient} instance should be derived and provided to the tree, effectively
 * clearing the urql cache.
 */
const subscribe = (listener: () => void) => {
  if (listeners.size >= 3) {
    logger.warn(
      'There are more than 3 subscribers to the urql cache version. This may indicate a ' +
        'memory leak if subscribers are not properly unsubscribing.',
      { numSubscribers: listeners.size + 1 },
    );
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => version;

/**
 * Increments the urql cache version, {@link version}, notifying the {@link UrqlGraphQlProvider}
 * that a new {@link UrqlGraphqlClient} instance should be derived via `useMemo` and provided to the
 * tree, which has the effect of clearing the urql cache.
 *
 * This should be called after any operation that changes the user's identity context (e.g. role
 * selection, entering an org as admin) to prevent stale cache data from leaking across sessions.
 *
 * This is necessary because the {@link UrqlProvider} is intentionally wrapped around the
 * inner authenticated-user provider so GraphQL can be used while resolving the authenticated user.
 * As a result, identity-context changes do not automatically remount the provider with a fresh
 * client, so we cannot rely on provider placement or a keyed remount alone to clear cached data
 * between sessions and must manually trigger the change.
 *
 * A plain module function is used instead of a React callback embedded inside of a context tree
 * primarily for purposes of avoiding "Tear Prevention".
 *
 * @see {@link UrqlGraphQlProvider} for more details on Tear Prevention and why this function is
 * implemented as a plain module function.
 */
export const invalidateUrqlCache = () => {
  version++;
  listeners.forEach(fn => fn());
};

export { getSnapshot as getUrqlCacheVersion, subscribe as subscribeToUrqlCacheVersion };
